import {
  Button,
  Container,
  Form,
  Icons,
  Option,
  RangePicker,
  Select,
  Tooltip,
} from "@ster/ster-toolkit";
import { useForm } from "antd/lib/form/Form";
import { Store } from "antd/lib/form/interface";
import classNames from "classnames";
import React, { useCallback, useEffect, useMemo } from "react";
import { useRecoilValue } from "recoil";

import { SterWebsiteModelPriceCompareLabelValuePair } from "./api";
import { metadataState } from "./atoms";
import styles from "./CampaignForm.module.less";
import { Campaign, SubCampaign } from "./Compare";
import SubCampaignForm from "./SubCampaignForm";

const emptyChannels: SterWebsiteModelPriceCompareLabelValuePair[] = [];

const CampaignForm = ({
  onlyLabels,
  campaign,
  unavailableOperators,
  onChange,
  onDelete,
  canDelete,
  medium,
  isSlave,
}: {
  onlyLabels: boolean;
  campaign: Partial<Campaign>;
  unavailableOperators?: string[];
  onChange: (updatedCampaign: Campaign) => void;
  onDelete: (id: number) => void;
  canDelete: boolean;
  medium: string;
  isSlave?: boolean;
}): JSX.Element => {
  const [form] = useForm<Campaign>();
  const metadata = useRecoilValue(metadataState(medium));

  const handleAddSubCampaign = useCallback(() => {
    onChange({
      ...(campaign as Campaign),
      subCampaigns: [
        ...(campaign.subCampaigns ?? []),
        {
          id:
            ((campaign.subCampaigns && campaign.subCampaigns?.length > 0
              ? campaign.subCampaigns[campaign.subCampaigns.length - 1]?.id
              : 0) ?? 0) + 1,
        } as SubCampaign,
      ],
    });
  }, [campaign, onChange]);

  const handleDeleteSubCampaign = useCallback(
    (id: number) => {
      onChange({
        ...(campaign as Campaign),
        subCampaigns: campaign.subCampaigns?.filter((s) => s.id !== id) ?? [],
      });
    },
    [campaign, onChange]
  );

  const handleChangeSubCampaign = useCallback(
    (subCampaign: SubCampaign) => {
      if (!campaign.subCampaigns) {
        return;
      }

      const index = campaign.subCampaigns
        .map(({ id }) => id)
        .indexOf(subCampaign.id);

      onChange({
        ...(campaign as Campaign),
        subCampaigns: [
          ...campaign.subCampaigns.slice(0, index),
          { ...subCampaign },
          ...campaign.subCampaigns.slice(index + 1),
        ],
      });
    },
    [campaign, onChange]
  );

  useEffect(() => {
    form.setFieldsValue({
      period: campaign.period,
      channels: campaign.channels,
    });
  }, [campaign, campaign.channels, campaign.period, form]);

  const channels = useMemo(
    () =>
      metadata.operators.find((o) => o.value === campaign.operator)?.channels ??
      emptyChannels,
    [campaign.operator, metadata.operators]
  );

  const handleFormChange = useCallback(
    (changedValues: Store, _: Store) => {
      const defaultValues: Partial<Campaign> = {};
      if (changedValues.operator) {
        const channelsForOperator =
          metadata.operators.find((o) => o.value === changedValues.operator)
            ?.channels ?? emptyChannels;
        if (medium === "tv") {
          defaultValues.channels = channelsForOperator.map((c) => c.value);
        } else {
          defaultValues.channels =
            channelsForOperator?.length === 1
              ? [channelsForOperator[0].value]
              : undefined;
        }
      }

      onChange({
        ...campaign,
        ...(changedValues as Campaign),
        ...defaultValues,
      });
    },
    [campaign, medium, metadata.operators, onChange]
  );
  return (
    <>
      <Container
        className={classNames(styles.container, {
          [styles.noStyle]: onlyLabels,
        })}
      >
        {canDelete && (
          <Tooltip title="Verwijder dit pakket">
            <button
              className={styles.delete}
              type="button"
              onClick={(): void => onDelete(campaign.id ?? 0)}
            >
              <Icons.DeleteIcon />
            </button>
          </Tooltip>
        )}
        <Form
          form={form}
          layout="horizontal"
          className={classNames("fixedWidthLabels", styles.form, {
            [styles.hideLabels]: !onlyLabels,
            [styles.hideInput]: onlyLabels,
          })}
          onValuesChange={handleFormChange}
          labelAlign="right"
        >
          <Form.Item name="operator" label="Exploitant">
            <Select
              placeholder="Kies een exploitant"
              options={metadata.operators.filter(
                (s) =>
                  medium === "radio" || !unavailableOperators?.includes(s.value)
              )}
            />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const operator = getFieldValue("operator");
              return (
                <Form.Item name="channels" label="Zenders">
                  <Select.Multi
                    disabled={!operator || channels.length <= 1}
                    singleLine={false}
                    placeholder="Kies je zender(s)"
                    treeData={channels}
                    maxTagCount="responsive"
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item name="period" label="Periode">
            <RangePicker disabled={isSlave} />
          </Form.Item>
          <Form.Item
            name="targetGroup"
            label="Inkoopdoelgroep"
            helpText={
              <>
                Kies hier voor de inkoopdoelgroep die op je voorstel vermeld
                staat. Per exploitant kan deze doelgroep verschillen. Dit kan
                bijvoorbeeld zijn 25-67 of 25-54 jaar zijn. Na het invullen van
                alle velden, krijg je de optie om de vergelijking ook op jouw
                communicatiedoelgroep(en) door te laten rekenen.
              </>
            }
          >
            <Select
              showSearch
              placeholder="Kies een doelgroep"
              maxTagCount="responsive"
            >
              {metadata.targetGroups.map((p) => (
                <Option value={p.value} title={p.helpText} key={p.value}>
                  {p.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Container>
      {campaign.subCampaigns?.map((subCampaign, index) => (
        <SubCampaignForm
          onlyLabels={false}
          subCampaign={subCampaign}
          onChange={handleChangeSubCampaign}
          onDelete={handleDeleteSubCampaign}
          canDelete={index !== 0}
          medium={medium}
          isFirst={index === 0}
          key={subCampaign.id}
        />
      ))}
      <Button
        mode="tertiary"
        onClick={handleAddSubCampaign}
        className={styles.addPackage}
        hidden={onlyLabels}
      >
        + Tijdvak toevoegen
      </Button>
    </>
  );
};

export default CampaignForm;
